'use client';

/**
 * Trial countdown banner.
 *
 * Shows under the top nav whenever the org is on the 7-day trial
 * (`plan === 'trial'`) until the user converts or it lapses.  Copy
 * sharpens to a warning tone in the last 48 hours per
 * users-me.json:frontend_usage and auth-register.json:frontend_usage.
 *
 * Returns null after expiry — once `trial_expires_at` is in the past the
 * backend's 402 trial_expired handler is the source of truth (the
 * /subscribe redirect happens via useGlobalApiErrorHandler).
 */
import { Clock } from 'lucide-react';
import Link from 'next/link';

import type { UserMeOrg } from '@/lib/auth';

interface TrialCountdownBannerProps {
  org: UserMeOrg;
}

export function TrialCountdownBanner({ org }: TrialCountdownBannerProps) {
  if (org.plan !== 'trial' || !org.trial_expires_at) return null;

  const expiresMs = new Date(org.trial_expires_at).getTime();
  if (!Number.isFinite(expiresMs)) return null;

  const now = Date.now();
  if (expiresMs <= now) return null;

  const dayMs = 24 * 60 * 60 * 1000;
  const daysLeft = Math.max(1, Math.ceil((expiresMs - now) / dayMs));
  const isUrgent = daysLeft <= 2;

  const tone = isUrgent
    ? 'border-warning-600/30 bg-warning-50 text-warning-900'
    : 'border-brand-primary-200 bg-brand-primary-50 text-brand-primary-900';

  const linkTone = isUrgent
    ? 'text-warning-900 hover:text-warning-700'
    : 'text-brand-primary-700 hover:text-brand-primary-800';

  return (
    <div
      className={`flex items-center justify-between gap-3 border-b px-4 py-2 text-sm ${tone}`}
      role="status"
    >
      <div className="flex items-center gap-2">
        <Clock aria-hidden size={16} strokeWidth={1.75} />
        <span>
          {isUrgent
            ? `Trial ends in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} — choose a plan to keep your work.`
            : `${daysLeft} days left in your trial.`}
        </span>
      </div>
      <Link
        className={`text-sm font-medium underline-offset-2 hover:underline ${linkTone}`}
        href="/billing"
      >
        Choose a plan
      </Link>
    </div>
  );
}
